import axios, { type AxiosError } from 'axios';
import { supabase } from '@/lib/supabase';
import type {
  Agent,
  BotAuthResponse,
  CreateAgentPayload,
  UpdateAgentPayload,
  AgentStatus,
  CallLog,
  CallStatsResponse,
  CreateCallLogPayload,
  UpdateCallLogPayload,
  DiarizedTranscriptChunk,
  MeetingSession,
  PhoneNumberMapping,
  TwilioAccount,
  ContextFile,
  Contact,
  ContactList,
  Campaign,
  CampaignTargetRow,
  DncEntry,
  EnrichJobAck,
  EnrichJobState,
  WebsiteCorpus,
  OnboardingState,
} from '@/types';

/* ------------------------------------------------------------------ */
/*  Axios instance                                                     */
/* ------------------------------------------------------------------ */

const http = axios.create({ baseURL: '/api' });

// Request interceptor: attach the Supabase access token on every request.
http.interceptors.request.use(async (config) => {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (session?.access_token) {
    config.headers.Authorization = `Bearer ${session.access_token}`;
  }
  return config;
});

// Response interceptor: unwrap API errors into readable messages.
// Routes that return `{ error: { code, message, details? } }` (e.g. agents.ts
// SLUG_*, billing.ts INSUFFICIENT_BALANCE) need the `code` + `details`
// preserved so callers can branch on it; routes that return `{ error: "<string>" }`
// still flow through as the message.
//
// E1-S6: every error carries the `x-request-id` header (set by the server
// middleware) so users can quote it when reporting issues.
http.interceptors.response.use(
  (res) => res,
  (
    error: AxiosError<{
      error?: string | { code?: string; message?: string; details?: unknown };
    }>,
  ) => {
    const raw = error.response?.data?.error;
    let code: string | undefined;
    let msg: string;
    let details: unknown;
    if (typeof raw === 'string') {
      msg = raw;
    } else if (raw && typeof raw === 'object') {
      code = raw.code;
      msg = raw.message ?? error.message ?? 'Request failed';
      details = raw.details;
    } else {
      msg = error.message ?? 'Network error';
    }
    const requestId = extractRequestId(error);
    const status = error.response?.status;
    const wrapped = new Error(msg) as Error & {
      code?: string;
      requestId?: string;
      status?: number;
      details?: unknown;
    };
    if (code) wrapped.code = code;
    if (requestId) wrapped.requestId = requestId;
    if (status) wrapped.status = status;
    if (details !== undefined) wrapped.details = details;
    return Promise.reject(wrapped);
  },
);

function extractRequestId(error: AxiosError): string | undefined {
  const headers = error.response?.headers;
  if (!headers) return undefined;
  // Axios lowercases headers on the response.
  const direct =
    (headers as Record<string, string | string[] | undefined>)['x-request-id'] ??
    (headers as Record<string, string | string[] | undefined>)['X-Request-ID'];
  if (Array.isArray(direct)) return direct[0];
  return typeof direct === 'string' ? direct : undefined;
}

/* ------------------------------------------------------------------ */
/*  Shared sub-types exposed across endpoints                          */
/* ------------------------------------------------------------------ */

/** One entry in the Admin Overview leaderboards (top-10 by minutes,
 *  agents, calls, or referrals). `value` is the metric-specific
 *  number; `hint` is an optional companion display (e.g. agents'
 *  newest company_name). Re-exported so the Overview tab component
 *  doesn't have to redeclare the shape. */
export interface AdminLeaderboardEntry {
  tenant_id: string;
  email: string | null;
  display_name: string;
  value: number;
  hint?: string | null;
}

/** Row in the Admin → Tenants list. See services/adminTenants.ts.
 *  Re-exported so the Tenants tab + drill-down page share one shape. */
export interface AdminTenantListRow {
  tenant_id: string;
  owner_user_id: string;
  email: string | null;
  display_name: string;
  signed_up_at: string;
  is_partner: boolean;
  referred_by_tenant_id: string | null;
  balance_cents: number;
  agent_count: number;
  total_calls: number;
  total_minutes: number;
  last_active_at: string | null;
  total_spent_cents: number;
  topup_count: number;
}

/** Full drill-down for /admin/tenants/:id. */
export interface AdminTenantProfile {
  tenant_id: string;
  owner_user_id: string;
  email: string | null;
  display_name: string;
  signed_up_at: string;
  is_partner: boolean;
  balance_cents: number;
  stats: {
    agent_count: number;
    total_calls: number;
    total_minutes: number;
    total_spent_cents: number;
    last_active_at: string | null;
  };
  referral: {
    referred_by: { tenant_id: string; email: string | null } | null;
    referred_via_agent: { id: string; name: string | null } | null;
    referred_in_count: number;
  };
  agents: {
    id: string;
    name: string | null;
    company_name: string | null;
    status: string | null;
    has_slug: boolean;
    has_embed: boolean;
    has_meeting: boolean;
    has_phone: boolean;
    created_at: string;
    updated_at: string | null;
  }[];
  recent_calls: {
    id: string;
    agent_id: string | null;
    agent_name: string | null;
    direction: string;
    status: string | null;
    duration_seconds: number | null;
    started_at: string;
    source: string | null;
  }[];
  recent_audit: {
    id: string;
    kind: string;
    subject_type: string | null;
    subject_id: string | null;
    details: unknown;
    created_at: string;
  }[];
  recent_spend: {
    id: string;
    kind: string;
    amount_cents: number;
    description: string | null;
    created_at: string;
  }[];
}

/* ------------------------------------------------------------------ */
/*  API methods                                                        */
/* ------------------------------------------------------------------ */

const api = {
  agents: {
    list: (signal?: AbortSignal): Promise<Agent[]> =>
      http.get<Agent[]>('/agents', { signal }).then((r) => r.data),

    get: (id: string, signal?: AbortSignal): Promise<Agent> =>
      http.get<Agent>(`/agents/${id}`, { signal }).then((r) => r.data),

    create: (data: CreateAgentPayload): Promise<Agent> =>
      http.post<Agent>('/agents', data).then((r) => r.data),

    /**
     * Simplified onboarding create. Scrapes `url`, distills a Cheat
     * Sheet, assigns a persona + the chosen skill template, and inserts
     * a ready-to-test, cheat-sheet-only agent (database_search_enabled
     * stays false). `role` must be one of the four static skills — the
     * Welcome page routes "Other" to the full builder instead. Slow
     * (scrape + distill, ~10–30s); the caller shows a stepped spinner.
     */
    fromWebsite: (
      payload: {
        url: string;
        role: 'sales_rep' | 'receptionist' | 'customer_support' | 'website_bot';
      },
      signal?: AbortSignal,
    ): Promise<{ agent: Agent; pagesScraped: number; cheatSheetGenerated: boolean }> =>
      http
        .post<{
          agent: Agent;
          pagesScraped: number;
          cheatSheetGenerated: boolean;
        }>('/agents/from-website', payload, { signal })
        .then((r) => r.data),

    update: (id: string, data: UpdateAgentPayload): Promise<Agent> =>
      http.put<Agent>(`/agents/${id}`, data).then((r) => r.data),

    delete: (id: string): Promise<void> => http.delete(`/agents/${id}`).then(() => undefined),

    updateStatus: (id: string, status: AgentStatus): Promise<Agent> =>
      http.patch<Agent>(`/agents/${id}/status`, { status }).then((r) => r.data),

    /**
     * Auto-publish — generates a public_slug if the agent doesn't
     * already have one. Idempotent (existing slug stays). Powers the
     * row-level Publish toggle on the Agents list.
     */
    publish: (id: string): Promise<Agent> =>
      http.post<Agent>(`/agents/${id}/publish`).then((r) => r.data),

    /** Clears the public_slug. Idempotent. Status (active/draft) is
     *  unaffected — unpublish only takes the public share link
     *  offline. */
    unpublish: (id: string): Promise<Agent> =>
      http.delete<Agent>(`/agents/${id}/publish`).then((r) => r.data),

    /**
     * Phase E7B-EMBED: avatar upload. Multipart with field name
     * `avatar`; server validates magic bytes (PNG/JPEG/WebP only) and
     * caps at 1 MB. Server-side bucket `agent-avatars` is bootstrapped
     * lazily on first write. Returns the updated agent row with the
     * new `avatar_url` populated.
     */
    uploadAvatar: (id: string, file: File): Promise<Agent> => {
      const fd = new FormData();
      fd.append('avatar', file);
      return http
        .post<Agent>(`/agents/${id}/avatar`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data);
    },

    /** Phase E7B-EMBED: clear the avatar. Idempotent — returns the
     *  row whether or not an avatar was set. */
    deleteAvatar: (id: string): Promise<Agent> =>
      http.delete<Agent>(`/agents/${id}/avatar`).then((r) => r.data),

    /**
     * Phase E7B-EMBED: rotate the embed publishable + secret keys.
     * Returns the SECRET ONCE — only sha256(secret) persists. The
     * caller must surface the secret in a copy-only modal and warn
     * the operator that re-fetching won't show it again.
     */
    rotateEmbedKeys: (
      id: string,
    ): Promise<{
      embedPublishableKey: string;
      embedSecretKey: string;
      embedKeysRotatedAt: string;
      agent: Agent;
    }> =>
      http
        .post<{
          embedPublishableKey: string;
          embedSecretKey: string;
          embedKeysRotatedAt: string;
          agent: Agent;
        }>(`/agents/${id}/embed/keys/rotate`)
        .then((r) => r.data),

    /**
     * Phase 1b: per-agent calendar connection. The connect endpoint
     * returns an OAuth authorize URL; the SPA navigates the browser to
     * it. Disconnect calls Recall to drop the calendar then nulls our
     * agents row.
     */
    calendar: {
      /** Returns the Google/Microsoft consent screen URL we should
       *  redirect the user's browser to. */
      connect: (
        agentId: string,
        provider: 'google_calendar' | 'microsoft_outlook',
      ): Promise<{ authorize_url: string }> =>
        http
          .get<{ authorize_url: string }>(`/agents/${agentId}/calendar/connect/${provider}`)
          .then((r) => r.data),
      disconnect: (agentId: string): Promise<{ ok: true }> =>
        http.delete<{ ok: true }>(`/agents/${agentId}/calendar`).then((r) => r.data),
    },

    /** Mint a single-use WebSocket ticket for the in-app voice test
     *  (Playground). The server consumes the ticket on /ws/gemini-live
     *  upgrade, fetches the agent, builds the Gemini setup, inserts the
     *  call_logs row, and arms both watchdogs — so the per-agent
     *  `max_call_duration_seconds` cap applies and Vertex session
     *  resumption works past the 10-min boundary. The token expires in
     *  60s; mint right before opening the WS. */
    webToken: (id: string): Promise<{ ticket: string; sessionId: string; agentId: string }> =>
      http
        .post<{ ticket: string; sessionId: string; agentId: string }>(`/agents/${id}/web-token`)
        .then((r) => r.data),

    /**
     * Convert an operator's plain-English description into a structured
     * AGENTS.md draft (XML-flavored prompt blocks: product / audience /
     * objective / do / don't / etc.). Powers the optional "Describe your
     * agent" generator above the System Prompt textarea in AgentBuilder.
     * The platform SOUL.md + USER.md layers run on top of this at runtime,
     * so the output deliberately omits voice rules / cold-call psychology.
     */
    generateInstructions: (
      description: string,
      ctx: { agent_name?: string; company_name?: string } = {},
      signal?: AbortSignal,
    ): Promise<{ instructions: string }> =>
      http
        .post<{
          instructions: string;
        }>(
          '/agents/generate-instructions',
          { description, agent_name: ctx.agent_name, company_name: ctx.company_name },
          { signal },
        )
        .then((r) => r.data),

    /**
     * GET /api/agents/skill-templates — fetch the four pre-built 9-block
     * AGENTS.md templates the Identity tile renders into `system_prompt`
     * on a card click. The 5th card ("Other") routes through
     * `generateInstructions` instead — there's no static template for it.
     */
    getSkillTemplates: (): Promise<{
      cards: {
        id: string;
        label: string;
        oneLiner: string;
        defaultCaptureFields: string[];
      }[];
      templates: Record<string, string>;
    }> =>
      http
        .get<{
          cards: {
            id: string;
            label: string;
            oneLiner: string;
            defaultCaptureFields: string[];
          }[];
          templates: Record<string, string>;
        }>('/agents/skill-templates')
        .then((r) => r.data),
  },

  calls: {
    list: (
      filter: {
        agent_id?: string;
        campaign_id?: string;
        /** When `true`, restrict to campaign-linked calls. Drives the
         *  Sequences tab's "All sequences" picker entry so it doesn't
         *  surface ad-hoc / web / inbound calls. */
        has_sequence?: boolean;
        qualified?: boolean;
        directions?: ('web' | 'inbound' | 'outbound')[];
        dispositions?: string[];
        from?: string;
        to?: string;
        page?: number;
        per_page?: number;
      } = {},
      signal?: AbortSignal,
    ): Promise<CallLog[]> => {
      const params: Record<string, string | number | undefined> = {
        agent_id: filter.agent_id,
        campaign_id: filter.campaign_id,
        has_sequence: filter.has_sequence ? 'true' : undefined,
        // Tri-state: undefined = no filter, 'true'/'false' = explicit.
        qualified: filter.qualified === undefined ? undefined : filter.qualified ? 'true' : 'false',
        direction: filter.directions?.join(','),
        disposition: filter.dispositions?.join(','),
        from: filter.from,
        to: filter.to,
        page: filter.page,
        per_page: filter.per_page,
      };
      // Drop undefined keys so axios doesn't append empty params.
      const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined));
      return http.get<CallLog[]>('/calls', { params: clean, signal }).then((r) => r.data);
    },

    /** Distinct deleted-agent snapshots that still own call history
     *  for the caller's tenant. Powers the Agents tab picker's
     *  "(deleted)" entries. */
    deletedAgents: (
      signal?: AbortSignal,
    ): Promise<{
      items: {
        name: string;
        company_name: string | null;
        latest_deleted_at: string | null;
        call_count: number;
      }[];
    }> => http.get('/calls/deleted-agents', { signal }).then((r) => r.data),

    get: (id: string, signal?: AbortSignal): Promise<CallLog> =>
      http.get<CallLog>(`/calls/${id}`, { signal }).then((r) => r.data),

    create: (data: CreateCallLogPayload): Promise<CallLog> =>
      http.post<CallLog>('/calls', data).then((r) => r.data),

    update: (id: string, data: UpdateCallLogPayload): Promise<CallLog> =>
      http.patch<CallLog>(`/calls/${id}`, data).then((r) => r.data),

    /** E10-S4 Dashboard aggregates. Omit `from` / `to` for the default
     *  last-30-days window. */
    stats: (
      params: {
        from?: string;
        to?: string;
        agent_id?: string;
        campaign_id?: string;
        has_sequence?: 'true';
      } = {},
      signal?: AbortSignal,
    ): Promise<CallStatsResponse> =>
      http.get<CallStatsResponse>('/calls/stats', { params, signal }).then((r) => r.data),

    /**
     * Wave 5b — fire-and-forget. POST kicks off the post-call pipeline
     * in the background and returns immediately with the freshly-created
     * job state (or the existing one when a job is already running for
     * this tenant). The UI then polls `enrichPendingStatus` every couple
     * seconds; `state.done === true` ends the poll loop.
     */
    enrichPending: (): Promise<EnrichJobAck> =>
      http.post('/calls/enrich-pending').then((r) => r.data),

    /** Poll target for the in-flight `enrichPending` job. `state` is
     *  null when no job has been started in the retain window. */
    enrichPendingStatus: (signal?: AbortSignal): Promise<{ state: EnrichJobState | null }> =>
      http.get('/calls/enrich-pending/status', { signal }).then((r) => r.data),
  },

  phone: {
    listNumbers: (signal?: AbortSignal): Promise<PhoneNumberMapping[]> =>
      http.get<PhoneNumberMapping[]>('/phone/numbers', { signal }).then((r) => r.data),

    addNumber: (
      phoneNumber: string,
      friendlyName?: string,
      twilioAccountId?: string,
    ): Promise<PhoneNumberMapping> =>
      http
        .post<PhoneNumberMapping>('/phone/numbers', {
          phone_number: phoneNumber,
          friendly_name: friendlyName,
          twilio_account_id: twilioAccountId,
        })
        .then((r) => r.data),

    removeNumber: (phoneNumber: string): Promise<void> =>
      http.delete('/phone/numbers', { data: { phone_number: phoneNumber } }).then(() => undefined),

    assign: (phoneNumber: string, agentId: string): Promise<void> =>
      http
        .post('/phone/assign', { phone_number: phoneNumber, agent_id: agentId })
        .then(() => undefined),

    unassign: (phoneNumber: string): Promise<void> =>
      http.post('/phone/unassign', { phone_number: phoneNumber }).then(() => undefined),

    callMe: (agentId: string, toNumber: string): Promise<void> =>
      http.post('/phone/call-me', { agent_id: agentId, to_number: toNumber }).then(() => undefined),
  },

  profile: {
    get: (signal?: AbortSignal) =>
      http
        .get<{
          email: string;
          phone: string;
          display_name: string;
          tenant_id: string | null;
          is_partner: boolean;
        }>('/profile', { signal })
        .then((r) => r.data),

    update: (data: { display_name?: string; phone?: string }) =>
      http
        .patch<{
          email: string;
          phone: string;
          display_name: string;
          tenant_id: string | null;
          is_partner: boolean;
        }>('/profile', data)
        .then((r) => r.data),
  },

  /** Onboarding milestone tracking — see /setup page. */
  onboarding: {
    get: (signal?: AbortSignal): Promise<OnboardingState> =>
      http.get<OnboardingState>('/onboarding', { signal }).then((r) => r.data),

    /** Mark a control-panel sub-flag — visited_dashboard / visited_calls
     *  / visited_contacts / visited_billing. Fires from the matching
     *  page mount. Idempotent. */
    markVisit: (
      section: 'visited_dashboard' | 'visited_calls' | 'visited_contacts' | 'visited_billing',
    ): Promise<OnboardingState> =>
      http.post<OnboardingState>(`/onboarding/visit/${section}`).then((r) => r.data),

    dismiss: (): Promise<OnboardingState> =>
      http.post<OnboardingState>('/onboarding/dismiss').then((r) => r.data),

    /** Phase 7 referral conversion. Called once on first authed visit
     *  after a signup that originated from a partner slug-page CTA
     *  (/signup?inherit_agent=<id> persisted via localStorage through
     *  the magic-link round-trip). Server stamps referral attribution
     *  on the new tenant + clones the demo agent. Idempotent on both
     *  the referral fields and the clone — safe to call again. */
    inheritAgent: (
      agentId: string,
    ): Promise<{ cloned_agent_id: string; already_existed: boolean }> =>
      http.post('/onboarding/inherit-agent', { agent_id: agentId }).then((r) => r.data),
  },

  /** Meeting-bot lifecycle — Phase 1 of MERIDIAN_MEETING_BOT_PLAN.md.
   *  start/get/disconnect are authed; botAuth is the public single-use
   *  nonce exchange the bot page makes on mount. */
  meetings: {
    start: (payload: {
      agent_id: string;
      meeting_url: string;
      mode?: 'participant' | 'listener';
    }): Promise<{
      meeting_id: string;
      recall_bot_id: string;
      status: string;
      meeting_platform: string;
      meeting_mode: string;
    }> => http.post('/meetings/start', payload).then((r) => r.data),

    get: (id: string, signal?: AbortSignal): Promise<{ session: MeetingSession }> =>
      http.get(`/meetings/${id}`, { signal }).then((r) => r.data),

    disconnect: (id: string): Promise<{ status: string }> =>
      http.post(`/meetings/${id}/disconnect`).then((r) => r.data),

    /** Flip a live meeting between 'participant' (agent speaks) and
     *  'listener' (note-taker, no audio out). The server persists the
     *  change to `meeting_sessions.meeting_mode` AND — if the meeting's
     *  WS proxy session is live in the same process — flips the
     *  in-memory audio gate and pushes a `clientContent` system override
     *  into the live Vertex stream so the model stops/resumes audio.
     *  `applied: false` means the DB write happened but no live WS
     *  session received the runtime side effects (reconnect will pick
     *  up the new mode from the DB). */
    setMode: (
      id: string,
      mode: 'participant' | 'listener',
    ): Promise<{ mode: 'participant' | 'listener'; applied: boolean }> =>
      http.post(`/meetings/${id}/mode`, { mode }).then((r) => r.data),

    /** Read the diarized rolling transcript chunks for a meeting.
     *  Merges Recall's per-participant streaming chunks (humans) with
     *  the agent's `outputTranscription`-derived chunks (Gemini),
     *  sorted ascending by start time. */
    transcript: (
      id: string,
      signal?: AbortSignal,
    ): Promise<{ chunks: DiarizedTranscriptChunk[] }> =>
      http.get(`/meetings/${id}/transcript`, { signal }).then((r) => r.data),

    /** Public — nonce IS the auth. Used only by the /meeting-bot route.
     *  Uses raw fetch to bypass the axios interceptor that calls
     *  `supabase.auth.getSession()` on every request. The bot page runs
     *  in Recall's headless Chromium with no Supabase session at all,
     *  and getSession() can hang in that environment — sending the bot
     *  page into a permanent "loading" state instead of failing fast. */
    botAuth: async (nonce: string): Promise<BotAuthResponse> => {
      const res = await fetch(`/api/meetings/bot-auth/${encodeURIComponent(nonce)}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) {
        let body: unknown = null;
        try {
          body = await res.json();
        } catch {
          /* ignore */
        }
        const message =
          (body && typeof body === 'object' && 'error' in body
            ? typeof (body as { error: { message?: string } }).error?.message === 'string'
              ? (body as { error: { message: string } }).error.message
              : null
            : null) ?? `HTTP ${res.status}`;
        throw new Error(message);
      }
      return (await res.json()) as BotAuthResponse;
    },
  },

  twilioAccounts: {
    list: (signal?: AbortSignal): Promise<TwilioAccount[]> =>
      http.get<TwilioAccount[]>('/twilio-accounts', { signal }).then((r) => r.data),

    create: (data: {
      account_sid: string;
      auth_token: string;
      friendly_name?: string;
    }): Promise<TwilioAccount> =>
      http.post<TwilioAccount>('/twilio-accounts', data).then((r) => r.data),

    delete: (id: string): Promise<void> =>
      http.delete(`/twilio-accounts/${id}`).then(() => undefined),

    /** List the numbers the operator owns on this Twilio account, each
     *  tagged with whether Meridian has already claimed it. Used by the
     *  Twilio Accounts page picker to give one-click claim UX. */
    listNumbers: (
      id: string,
      signal?: AbortSignal,
    ): Promise<
      {
        phoneNumber: string;
        friendlyName: string | null;
        sid: string;
        voiceUrl: string | null;
        claimedByThisMeridian: boolean;
      }[]
    > => http.get(`/twilio-accounts/${id}/numbers`, { signal }).then((r) => r.data),

    /** Read regulatory / compliance status from the operator's Twilio
     *  account: Trust Hub, STIR/SHAKEN, Branded Calling. Used by the
     *  compliance card on the Twilio Accounts page. (A2P 10DLC dropped
     *  2026-05-13 — Meridian is voice-only.) */
    getCompliance: (
      id: string,
      signal?: AbortSignal,
    ): Promise<{
      accountType: 'trial' | 'full' | 'unknown';
      trustHub: 'approved' | 'pending' | 'not_started' | 'unknown';
      stirShaken: 'approved' | 'pending' | 'not_started' | 'unknown';
      brandedCalling: 'approved' | 'pending' | 'not_started' | 'unknown';
    }> => http.get(`/twilio-accounts/${id}/compliance`, { signal }).then((r) => r.data),
  },

  phoneNumbers: {
    search: (
      body: {
        country: string;
        areaCode?: string;
        contains?: string;
        capabilities?: { voice?: boolean; sms?: boolean };
        limit?: number;
      },
      signal?: AbortSignal,
    ): Promise<{
      numbers: {
        phoneNumber: string;
        friendlyName: string;
        locality: string | null;
        region: string | null;
        capabilities: { voice: boolean; sms: boolean; mms: boolean };
        monthlyCostCents: number;
      }[];
    }> => http.post('/phone/numbers/search', body, { signal }).then((r) => r.data),

    buy: (body: {
      agentId?: string | null;
      phoneNumber: string;
      friendlyName?: string;
    }): Promise<{
      mappingId: string;
      phoneNumber: string;
      twilioNumberSid: string;
      chargedCents: number;
      nextBillAt: string;
    }> => http.post('/phone/numbers/buy', body).then((r) => r.data),
  },

  admin: {
    /** Daily-driver dashboard data — see services/adminOverview.ts for
     *  query semantics. Window is one of 7/30/90 days. */
    overview: (
      windowDays: 7 | 30 | 90 = 7,
      signal?: AbortSignal,
    ): Promise<{
      window_days: 7 | 30 | 90;
      stats: {
        signups: number;
        wau: number;
        mau: number;
        calls: number;
        revenue_cents: number;
      };
      signups_daily: { day: string; count: number }[];
      activation_funnel: {
        signed_up: number;
        created_agent: number;
        published_agent: number;
        made_call: number;
        topped_up: number;
      };
      leaderboards: {
        by_minutes: AdminLeaderboardEntry[];
        by_agents: AdminLeaderboardEntry[];
        by_calls: AdminLeaderboardEntry[];
        by_referrals: AdminLeaderboardEntry[];
      };
    }> => http.get('/admin/overview', { params: { windowDays }, signal }).then((r) => r.data),

    /** All tenants (up to 200) with per-tenant metrics for the
     *  Tenants tab. Sorted by last activity desc server-side; client
     *  filters / sorts further. */
    tenants: (signal?: AbortSignal): Promise<{ rows: AdminTenantListRow[] }> =>
      http.get('/admin/tenants', { signal }).then((r) => r.data),

    /** Full drill-down for one tenant — header + stats + agents +
     *  recent calls + recent audit + recent spend + referral chain. */
    tenantProfile: (tenantId: string, signal?: AbortSignal): Promise<AdminTenantProfile> =>
      http
        .get(`/admin/tenants/${encodeURIComponent(tenantId)}/profile`, { signal })
        .then((r) => r.data),

    /** Lightweight gate probe used by the RequireAdmin route guard.
     *  Always returns 200; isAdmin = false replaces the old "render
     *  scaffold then 403" UX with a clean "Page Not Found" redirect. */
    me: (signal?: AbortSignal): Promise<{ isAdmin: boolean }> =>
      http.get<{ isAdmin: boolean }>('/admin/me', { signal }).then((r) => r.data),

    settings: (signal?: AbortSignal) =>
      http.get<Record<string, unknown>>('/admin/settings', { signal }).then((r) => r.data),
    updateSettings: (patch: Record<string, unknown>) =>
      http.patch<Record<string, unknown>>('/admin/settings', patch).then((r) => r.data),
    auditLog: (
      params: { cursor?: string; kind?: string; tenantId?: string; limit?: number } = {},
      signal?: AbortSignal,
    ) =>
      http
        .get<{
          items: {
            id: string;
            kind: string;
            subject_type: string | null;
            subject_id: string | null;
            details: unknown;
            tenant_id: string | null;
            user_id: string | null;
            agent_id: string | null;
            created_at: string;
          }[];
          nextCursor: string | null;
        }>('/admin/audit-log', { params, signal })
        .then((r) => r.data),
    rollups: (sinceDays = 30, signal?: AbortSignal) =>
      http
        .get<{
          sinceDays: number;
          totals: { callDebitCents: number; topupCents: number; rentalCents: number };
          perTenant: {
            tenantId: string;
            callDebitCents: number;
            topupCents: number;
            rentalCents: number;
          }[];
        }>('/admin/rollups', { params: { sinceDays }, signal })
        .then((r) => r.data),
    /** Revenue analytics: daily buckets of recognized revenue + cash
     *  collected for the window [from, to] (ISO). Paid-vs-free split
     *  uses the FIFO simplification documented in
     *  server/src/services/revenueAnalytics.ts. */
    revenueDaily: (params: { from: string; to: string }, signal?: AbortSignal) =>
      http
        .get<{
          buckets: {
            day: string;
            recognized_cents: number;
            recognized_paid_cents: number;
            recognized_free_cents: number;
            cash_collected_cents: number;
          }[];
        }>('/admin/revenue/daily', { params, signal })
        .then((r) => r.data),
    /** Top tenants by recognized revenue in the window. `limit` caps
     *  at 100 server-side; default 20. */
    topTenants: (params: { from: string; to: string; limit?: number }, signal?: AbortSignal) =>
      http
        .get<{
          items: {
            tenantId: string;
            email: string | null;
            displayName: string | null;
            createdAt: string;
            recognizedCents: number;
            cashCollectedCents: number;
          }[];
        }>('/admin/revenue/top-tenants', { params, signal })
        .then((r) => r.data),

    /** Daily cost-of-goods + recognized revenue + cash collected +
     *  margin. Cost components broken down: Twilio per-call,
     *  Twilio number rental, Gemini Live audio in / audio out / text
     *  in / text out (priced from per-call metadata at query time),
     *  post-call LLM (lead extractor today). */
    costAnalyticsDaily: (params: { from: string; to: string }, signal?: AbortSignal) =>
      http
        .get<{
          buckets: {
            day: string;
            twilio_call_cents: number;
            twilio_rental_cents: number;
            gemini_audio_in_cents: number;
            gemini_audio_out_cents: number;
            gemini_text_in_cents: number;
            gemini_text_out_cents: number;
            post_call_llm_cents: number;
            total_cost_cents: number;
            recognized_cents: number;
            cash_collected_cents: number;
            margin_cents: number;
            margin_pct: number | null;
          }[];
        }>('/admin/cost-analytics/daily', { params, signal })
        .then((r) => r.data),

    /** Read the merged Gemini price table (DB overrides on top of code
     *  defaults). `defaults` is what the code ships with; `current`
     *  is what's actually used at pricing time. */
    pricing: (signal?: AbortSignal) =>
      http
        .get<{
          defaults: Record<string, Record<string, number>>;
          current: Record<string, Record<string, number>>;
        }>('/admin/pricing', { signal })
        .then((r) => r.data),

    /** Replace the DB-stored Gemini price table. All values are cents
     *  per million tokens. Server validates non-negative numbers. */
    updatePricing: (gemini_pricing: Record<string, Record<string, number>>) =>
      http.put<{ ok: true }>('/admin/pricing', { gemini_pricing }).then((r) => r.data),

    /**
     * Admin "Call Quality" tab feed — last N calls (default 5) with their
     * voice scores, transcripts, and owner/agent metadata. Voice scores
     * are computed by the post-call pipeline; calls without a score yet
     * (in-flight, scoring failed) appear with `voice_score: null`.
     */
    callQualityRecent: (
      params: { limit?: number } = {},
      signal?: AbortSignal,
    ): Promise<{
      items: {
        id: string;
        direction: string | null;
        status: string | null;
        started_at: string | null;
        ended_at: string | null;
        duration_seconds: number | null;
        caller_number: string | null;
        callee_number: string | null;
        tenant_id: string | null;
        /** Owner email resolved via tenants.owner_user_id → auth.users.email.
         *  Pre-Wave-5 this was `tenant_email` and silently null because the
         *  server selected non-existent columns on `tenants`. */
        owner_email: string | null;
        agent_id: string | null;
        agent_name: string | null;
        agent_company: string | null;
        transcript: { speaker: string; text: string; startedAt?: string; endedAt?: string }[];
        voice_score: {
          overallScore: number;
          heuristicFails: number;
          judgeAvg: number;
          judgeFails: number;
          heuristicViolations: {
            agentTurnIdx: number;
            label: string;
            matched: string;
            excerpt: string;
          }[];
          judgeScores: { id: string; score: number; rationale: string }[];
          scoredAt: string;
          judgeFailed: boolean;
          judgeError?: string;
        } | null;
      }[];
    }> => http.get('/admin/call-quality/recent', { params, signal }).then((r) => r.data),

    /** Channel-partner management — see server/src/routes/admin.ts. */
    partners: {
      list: (
        signal?: AbortSignal,
      ): Promise<{
        partners: {
          tenant_id: string;
          owner_user_id: string;
          email: string | null;
          display_name: string;
          tagged_at: string;
          agent_count: number;
        }[];
      }> => http.get('/admin/partners', { signal }).then((r) => r.data),

      /** Browse all users (newest first) for the "see who has signed up
       *  and click to tag" admin flow. Returns up to `limit` rows
       *  (default 100, cap 500). Each row carries the current
       *  is_partner flag so the same list serves both as a directory
       *  and as the tagging surface. */
      listUsers: (
        limit = 100,
        signal?: AbortSignal,
      ): Promise<{
        users: {
          user_id: string;
          email: string | null;
          display_name: string;
          signed_up_at: string;
          tenant_id: string | null;
          is_partner: boolean;
          agent_count: number;
        }[];
      }> => http.get('/admin/users', { params: { limit }, signal }).then((r) => r.data),

      /** Returns `{found:false}` when no auth.users row matches the
       *  email. When found, includes the user's tenant_id (for the
       *  toggle PATCH) and current is_partner flag. */
      searchUser: (
        email: string,
        signal?: AbortSignal,
      ): Promise<
        | { found: false }
        | {
            found: true;
            user_id: string;
            email: string;
            tenant_id: string | null;
            is_partner: boolean;
          }
      > => http.get('/admin/users/search', { params: { email }, signal }).then((r) => r.data),

      /** Per-partner funnel view for the admin Partners tab. Same shape
       *  as `api.partners.funnel` (self-view); reuses the same SQL on
       *  the server. Admin-gated. */
      funnel: (
        tenantId: string,
        signal?: AbortSignal,
      ): Promise<{
        rows: {
          agent_id: string;
          agent_name: string | null;
          company_name: string | null;
          created_at: string;
          has_slug: boolean;
          has_embed: boolean;
          has_meeting: boolean;
          has_phone: boolean;
          /** TRUE for since-deleted agents — UI applies the
           *  "(deleted)" amber suffix. */
          is_deleted?: boolean;
          opens: number;
          unique_visitors: number;
          conversations: number;
          minutes_practiced: number;
          signups: number;
        }[];
        totals: {
          agents: number;
          opens: number;
          unique_visitors: number;
          conversations: number;
          minutes_practiced: number;
          signups: number;
        };
      }> =>
        http
          .get(`/admin/partners/${encodeURIComponent(tenantId)}/funnel`, { signal })
          .then((r) => r.data),

      /** Toggle the `tenants.is_partner` flag. Audit-logged server-
       *  side. Returns the new flag state. */
      togglePartner: (
        tenantId: string,
        isPartner: boolean,
      ): Promise<{ tenant_id: string; is_partner: boolean }> =>
        http
          .patch(`/admin/tenants/${tenantId}/partner`, { is_partner: isPartner })
          .then((r) => r.data),
    },
  },

  billing: {
    /** E11-S8: current tenant balance + rate card snapshot + volume
     *  brackets (if configured) + this-month usage. */
    balance: (
      signal?: AbortSignal,
    ): Promise<{
      balanceCents: number;
      lastTopupCents: number;
      lowBalanceThresholdCents: number;
      rateCentsPerMinute: number;
      rateInboundCentsPerMinute: number;
      rateOutboundCentsPerMinute: number;
      rateWebCentsPerMinute: number;
      numberRentalCentsPerMonth: number;
      signupBonusCreditedAt: string | null;
      volumeBrackets: { min_monthly_minutes: number; rate_cents_per_minute: number }[];
      monthlyBillableMinutes: number;
      currentBracket: { min_monthly_minutes: number; rate_cents_per_minute: number } | null;
      nextBracket: { min_monthly_minutes: number; rate_cents_per_minute: number } | null;
    }> => http.get('/billing/balance', { signal }).then((r) => r.data),

    /** E11-S2: create a one-off Stripe Checkout session for a top-up
     *  using the hosted (redirect) flow. Returns a URL the browser
     *  navigates to. */
    checkout: (amountCents: number): Promise<{ checkoutUrl: string; sessionId: string }> =>
      http.post('/billing/checkout', { amountCents, uiMode: 'hosted' }).then((r) => r.data),

    /** Embedded Stripe Checkout — returns a `clientSecret` consumed by
     *  `<EmbeddedCheckoutProvider>` so the flow renders in an in-app
     *  dialog instead of redirecting. */
    checkoutEmbedded: (amountCents: number): Promise<{ clientSecret: string; sessionId: string }> =>
      http.post('/billing/checkout', { amountCents, uiMode: 'embedded' }).then((r) => r.data),

    /** E11-S8: paginated ledger. `cursor` is the `created_at` of the last
     *  row from the previous page; omit for the first page. Optional
     *  `from` / `to` ISO bounds scope the window (BillingPage pairs this
     *  with the same DateRangePicker presets the Dashboard uses). */
    transactions: (
      params: { cursor?: string; limit?: number; from?: string; to?: string } = {},
      signal?: AbortSignal,
    ): Promise<{
      items: {
        id: string;
        amount_cents: number;
        kind: string;
        ref_type: string | null;
        ref_id: string | null;
        description: string | null;
        created_at: string;
        /** Enrichment on call_debit rows — the underlying call context so
         *  the UI can show Agent + direction + counterparty phone instead
         *  of the raw debit description. Absent on non-call_debit rows. */
        call?: {
          direction: string | null;
          caller_number: string | null;
          callee_number: string | null;
          duration_seconds: number | null;
          agent_name: string | null;
        };
      }[];
      nextCursor: string | null;
    }> => http.get('/billing/transactions', { params, signal }).then((r) => r.data),
  },

  documents: {
    extract: (files: File[]): Promise<ContextFile[]> => {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));
      return http
        .post<{ files: ContextFile[] }>('/documents/extract', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data.files);
    },

    ingestUrl: (
      url: string,
    ): Promise<{
      file: ContextFile;
      sourceUrl: string;
      partialExtraction: boolean;
    }> =>
      http
        .post<{
          file: ContextFile;
          sourceUrl: string;
          partialExtraction: boolean;
        }>('/documents/url', { url })
        .then((r) => r.data),

    discoverSubPages: (
      url: string,
    ): Promise<{
      seedUrl: string;
      source: 'sitemap' | 'html-links' | 'none';
      pages: { url: string; title: string | null; score: number; label: string | null }[];
    }> => http.post('/documents/url/discover', { url }).then((r) => r.data),

    buildWebsiteCorpus: (payload: {
      url: string;
      /** Pre-supplied URL list — present only on legacy callers. New
       *  AgentBuilder flow omits this and lets the server discover +
       *  curate the URL list end-to-end. */
      urls?: string[];
      /** Forwarded to the server-side LLM curator so it can make
       *  vertical-aware priority calls. */
      companyName?: string | null;
      systemPrompt?: string | null;
    }): Promise<{
      corpus: WebsiteCorpus;
      failures: { url: string; reason: string }[];
      /** Auto-generated Cheat Sheet draft (Gemini Pro). The
       *  AgentBuilder pre-fills the operator's `additional_context`
       *  textarea with this when it's non-empty AND the operator
       *  hasn't already typed something there. */
      cheatSheet: string;
    }> => http.post('/documents/url/corpus', payload).then((r) => r.data),

    /** Compress a multi-page website KB bundle to ≤ 20 KB if it's
     *  larger. Bundles already under that size pass through unchanged
     *  (no LLM call, `summarized` is false in the response). The
     *  agent's system_instruction has a hard ~32k working-memory budget
     *  on the native-audio model; bundles much bigger than 20 KB push
     *  the prompt past that budget and cause mid-call aborts. */
    summarizeBundle: (payload: {
      text: string;
      source_url: string;
    }): Promise<{
      text: string;
      original_bytes: number;
      final_bytes: number;
      summarized: boolean;
    }> => http.post('/documents/url/summarize-bundle', payload).then((r) => r.data),
  },

  /* ---------------- B3: contacts, campaigns, DNC ------------------- */

  contacts: {
    create: (payload: {
      first_name?: string;
      last_name?: string;
      phone: string;
      email?: string;
      company?: string;
      tags?: string[];
    }): Promise<Contact> => http.post('/contacts', payload).then((r) => r.data),

    list: (params?: {
      list_id?: string;
      q?: string;
      page?: number;
      per_page?: number;
    }): Promise<{ items: Contact[]; total: number; page: number; perPage: number }> =>
      http.get('/contacts', { params }).then((r) => r.data),

    get: (id: string): Promise<Contact> => http.get(`/contacts/${id}`).then((r) => r.data),

    /**
     * Wave 4b: partial update for the editable Contact detail page.
     * Server accepts a whitelisted subset of columns; empty strings
     * are coerced to null. Returns the full updated row.
     *
     * Wave 5 added the wall-clock pair (`next_action_local_dt` +
     * `next_action_local_tz`) — the dt is "YYYY-MM-DDTHH:MM:SS" with
     * NO Z and NO offset, server rejects strings with a zone tail.
     */
    update: (
      id: string,
      patch: Partial<{
        first_name: string | null;
        last_name: string | null;
        email: string | null;
        company: string | null;
        timezone: string | null;
        next_action: string | null;
        /** @deprecated Wave 5 — server still accepts this but new
         *  edits should write to next_action_local_dt instead. */
        next_action_at: string | null;
        next_action_local_dt: string | null;
        next_action_local_tz: string | null;
        /** Stored on contacts.custom.role — server merges into JSONB. */
        role: string | null;
        /** Stored on contacts.custom.notes — server merges into JSONB. */
        notes: string | null;
        /** Top-level JSONB array. Server accepts either an array or a
         *  string ("hot, founder, uk") and splits via /[,;|]/. */
        tags: string[] | string | null;
      }>,
    ): Promise<Contact> => http.patch(`/contacts/${id}`, patch).then((r) => r.data),

    /**
     * Wave 4b: every call_log placed/received against this contact's
     * phone number, sorted newest-first. Drives the transcript
     * timeline on the Contact detail page.
     */
    calls: (
      id: string,
    ): Promise<{
      items: {
        id: string;
        agent_id: string | null;
        agent_name: string | null;
        direction: string | null;
        status: string | null;
        caller_number: string | null;
        callee_number: string | null;
        duration_seconds: number | null;
        twilio_call_sid: string | null;
        started_at: string | null;
        ended_at: string | null;
        metadata: Record<string, unknown> | null;
      }[];
    }> => http.get(`/contacts/${id}/calls`).then((r) => r.data),

    delete: (id: string): Promise<void> => http.delete(`/contacts/${id}`).then(() => undefined),

    /**
     * Manual catch-up enrichment from the latest unenriched
     * transcripts. Mirrors `calls.enrichPending` but the "pending"
     * definition is contact-side (phone matches a contact AND that
     * contact's custom.history doesn't yet include the call_log_id).
     * Bounded server-side at 25 per request.
     */
    enrich: (): Promise<EnrichJobAck & { backfill_linked?: number }> =>
      http.post('/contacts/enrich').then((r) => r.data),

    /** Poll target for the in-flight `enrich` job. `state` is null when
     *  no job has been started in the retain window. */
    enrichStatus: (signal?: AbortSignal): Promise<{ state: EnrichJobState | null }> =>
      http.get('/contacts/enrich/status', { signal }).then((r) => r.data),

    /**
     * CSV import without a list — rows land on the All Contacts tab.
     * Same upsert semantics as the per-list import.
     */
    import: (
      file: File,
    ): Promise<{
      total: number;
      inserted: number;
      updated: number;
      skipped: { row: number; reason: string }[];
    }> => {
      const form = new FormData();
      form.append('file', file);
      return http
        .post('/contacts/import', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data);
    },
  },

  contactLists: {
    create: (payload: { name: string; description?: string }): Promise<ContactList> =>
      http.post('/contact-lists', payload).then((r) => r.data),

    list: (): Promise<{ items: ContactList[] }> => http.get('/contact-lists').then((r) => r.data),

    get: (id: string): Promise<ContactList> => http.get(`/contact-lists/${id}`).then((r) => r.data),

    /** Delete the list itself (rows in `contacts` are unaffected — they
     *  remain on the All Contacts tab). Server cascades the membership
     *  rows. */
    delete: (id: string): Promise<void> =>
      http.delete(`/contact-lists/${id}`).then(() => undefined),

    addMember: (listId: string, contactId: string): Promise<{ added: boolean }> =>
      http
        .post(`/contact-lists/${listId}/memberships`, { contact_id: contactId })
        .then((r) => r.data),

    /**
     * Bulk variant — used by the Contacts page "Add to list" toolbar
     * after multi-selecting rows. Server returns counts so the toast
     * can say "Added 12, skipped 3 (already in list)".
     */
    addMembers: (
      listId: string,
      contactIds: string[],
    ): Promise<{ requested: number; added: number; skipped: number }> =>
      http
        .post(`/contact-lists/${listId}/memberships`, { contact_ids: contactIds })
        .then((r) => r.data),

    importCsv: (
      listId: string,
      file: File,
    ): Promise<{
      total: number;
      inserted: number;
      updated: number;
      skipped: { row: number; reason: string }[];
    }> => {
      const form = new FormData();
      form.append('file', file);
      return http
        .post(`/contact-lists/${listId}/import`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data);
    },
  },

  campaigns: {
    create: (payload: {
      agent_id: string;
      contact_list_id: string;
      name: string;
      retry_policy?: unknown;
      call_window?: unknown;
      max_concurrency?: number;
      max_attempts_per_day?: number;
    }): Promise<Campaign> => http.post('/campaigns', payload).then((r) => r.data),

    list: (): Promise<{ items: Campaign[] }> => http.get('/campaigns').then((r) => r.data),

    get: (id: string): Promise<Campaign> => http.get(`/campaigns/${id}`).then((r) => r.data),

    transition: (
      id: string,
      action: 'schedule' | 'start' | 'pause' | 'resume' | 'stop',
    ): Promise<Campaign> => http.post(`/campaigns/${id}/${action}`).then((r) => r.data),

    rename: (id: string, name: string): Promise<Campaign> =>
      http.patch(`/campaigns/${id}`, { name }).then((r) => r.data),

    projectTargets: (id: string): Promise<{ inserted: number; total: number }> =>
      http.post(`/campaigns/${id}/project-targets`).then((r) => r.data),

    summary: (
      id: string,
    ): Promise<{
      state_counts: Record<string, number>;
      disposition_counts: { disposition: string | null; count: number; charged_cents: number }[];
    }> => http.get(`/campaigns/${id}/summary`).then((r) => r.data),

    /**
     * Returns the export CSV as a Blob. Goes through the axios instance
     * so the Supabase access token is attached — a plain `<a href>` click
     * cannot carry the Authorization header and would 401.
     */
    exportCsv: (id: string): Promise<Blob> =>
      http.get(`/campaigns/${id}/export.csv`, { responseType: 'blob' }).then((r) => r.data),

    /**
     * Per-target list for the detail page — one row per prospect with
     * state, attempts, disposition, and last-call metadata. Pagination
     * mirrors the contacts list (offset-based, capped server-side at 200).
     */
    listTargets: (
      id: string,
      params?: { page?: number; per_page?: number },
    ): Promise<{
      items: CampaignTargetRow[];
      total: number;
      page: number;
      perPage: number;
    }> => http.get(`/campaigns/${id}/targets`, { params }).then((r) => r.data),
  },

  dnc: {
    add: (payload: {
      phone: string;
      reason?: string;
      source?: 'manual' | 'opt_out' | 'dnc_registry_import';
    }): Promise<DncEntry> => http.post('/dnc', payload).then((r) => r.data),

    remove: (phone: string): Promise<void> =>
      http.delete('/dnc', { params: { phone } }).then(() => undefined),

    list: (params?: {
      page?: number;
      per_page?: number;
    }): Promise<{ items: DncEntry[]; total: number }> =>
      http.get('/dnc', { params }).then((r) => r.data),

    importCsv: (
      file: File,
    ): Promise<{ total: number; inserted: number; skipped: { row: number; reason: string }[] }> => {
      const form = new FormData();
      form.append('file', file);
      return http
        .post('/dnc/import', form, { headers: { 'Content-Type': 'multipart/form-data' } })
        .then((r) => r.data);
    },
  },

  /* ---------------- B4: handoff, KB jobs, recording, SLO ----------- */

  kb: {
    jobs: (
      agentId: string,
    ): Promise<{
      jobs: {
        id: string;
        state: 'queued' | 'running' | 'done' | 'failed';
        total_chunks: number;
        done_chunks: number;
        error: string | null;
        source_label: string | null;
        started_at: string | null;
        completed_at: string | null;
        created_at: string;
      }[];
      stats: { chunks: number; last_indexed_at: string | null };
    }> => http.get(`/agents/${agentId}/kb/jobs`).then((r) => r.data),

    reindex: (agentId: string): Promise<{ enqueued: { jobId: string; deduplicated: boolean }[] }> =>
      http.post(`/agents/${agentId}/kb/reindex`).then((r) => r.data),

    crawl: (payload: {
      agent_id: string;
      url: string;
      max_pages?: number;
      urls?: string[];
    }): Promise<{
      seed: string;
      totalIngested: number;
      skipped: number;
      pages: {
        url: string;
        ok: boolean;
        skippedReason?: string;
        jobId?: string;
        charsIngested?: number;
      }[];
    }> => http.post('/documents/url/crawl', payload).then((r) => r.data),
  },

  handoff: {
    create: (payload: {
      agent_id: string;
      to_email?: string;
      ttl_hours?: number;
    }): Promise<{ handoff_id: string; token: string; expires_at: string }> =>
      http.post('/handoff', payload).then((r) => r.data),

    preview: (
      token: string,
    ): Promise<{
      agent_name: string;
      to_email: string | null;
      expires_at: string;
      status: 'available' | 'claimed' | 'expired';
    }> => http.get(`/handoff/${encodeURIComponent(token)}/preview`).then((r) => r.data),

    claim: (token: string): Promise<{ claimed_agent_id: string; original_agent_id: string }> =>
      http.post(`/handoff/${encodeURIComponent(token)}/claim`).then((r) => r.data),
  },

  slo: {
    get: (
      windowHours = 24,
    ): Promise<{
      window_hours: number;
      p95_audio_rtt_ms: number | null;
      p95_claim_to_dial_ms: number | null;
      inbound_rate_limit_hits: number;
      orchestrator_workers_healthy: number;
      orchestrator_workers_total: number;
      captured_at: string;
    }> => http.get('/admin/slo', { params: { window_hours: windowHours } }).then((r) => r.data),
  },

  /** Channel-partner endpoints — gated server-side by `tenants.is_partner`.
   *  Admin tags a tenant as partner via `api.admin.partners.togglePartner`.
   *
   *  Two-step bulk-create flow:
   *    1. `preview(file)` parses the CSV and returns rows + issues. No write.
   *    2. `commit(rows, onEvent)` opens a streaming NDJSON connection;
   *       `onEvent` fires for each row as it lands and for the final
   *       summary, so the UI can render real-time progress (slow Other
   *       rows that hit the LLM drafter take a few seconds each). */
  partners: {
    preview: (
      file: File,
    ): Promise<{
      rows: {
        idx: number;
        company_name: string;
        role: string;
        cheat_sheet: string;
        website: string;
        role_description: string;
        issues: string[];
      }[];
    }> => {
      const form = new FormData();
      form.append('file', file);
      return http
        .post('/partners/agents/bulk/preview', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data);
    },

    /** Phase 8 partner dashboard data. Returns one row per agent the
     *  partner owns, plus aggregate totals. Powers PartnersPage's
     *  Analytics card. */
    funnel: (
      signal?: AbortSignal,
    ): Promise<{
      rows: {
        agent_id: string;
        agent_name: string | null;
        company_name: string | null;
        created_at: string;
        has_slug: boolean;
        has_embed: boolean;
        has_meeting: boolean;
        has_phone: boolean;
        /** TRUE for since-deleted agents — UI applies the "(deleted)"
         *  amber suffix and dims the channel icons. */
        is_deleted?: boolean;
        opens: number;
        unique_visitors: number;
        conversations: number;
        minutes_practiced: number;
        signups: number;
      }[];
      totals: {
        agents: number;
        opens: number;
        unique_visitors: number;
        conversations: number;
        minutes_practiced: number;
        signups: number;
      };
    }> => http.get('/partners/funnel', { signal }).then((r) => r.data),

    /** Stream commit. Throws on transport / auth failures (the row-level
     *  failures arrive via `row_skip` events, not exceptions). */
    commit: async (
      rows: {
        idx: number;
        company_name: string;
        role: string;
        cheat_sheet: string;
        website: string;
        role_description: string;
      }[],
      onEvent: (
        ev:
          | { type: 'row_start'; idx: number }
          | { type: 'row_done'; idx: number; agent_id: string }
          | { type: 'row_skip'; idx: number; reason: string }
          | { type: 'row_retry_queued'; idx: number; reason: string }
          | { type: 'summary'; total: number; created: number; skipped: number },
      ) => void,
      signal?: AbortSignal,
    ): Promise<void> => {
      // Pull the Supabase JWT the same way the axios interceptor does
      // (apiClient adds it via getSession()) — we have to do it manually
      // here because we're not using axios for the streaming response.
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const res = await fetch('/api/partners/agents/bulk/commit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ rows }),
        signal,
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `Commit failed (${res.status})`);
      }
      const reader = res.body?.getReader();
      if (!reader) throw new Error('No response stream');
      const decoder = new TextDecoder();
      let buf = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let nl: number;
        while ((nl = buf.indexOf('\n')) >= 0) {
          const line = buf.slice(0, nl).trim();
          buf = buf.slice(nl + 1);
          if (!line) continue;
          try {
            const ev = JSON.parse(line);
            onEvent(ev);
          } catch {
            // Skip malformed lines defensively — server only writes
            // valid JSON, but a truncated chunk during disconnect
            // could leave us with a partial.
          }
        }
      }
    },
  },
} as const;

export default api;
